// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect, useRef, useState } from 'react';
import { Archive, Chat } from '@carbon/icons-react';
import { useShellStore } from '../../stores/shellStore';
import { useChatStore } from '../../stores/chatStore';
import { useAskHeliosResize } from '../../hooks/useAskHeliosResize';
import AskHeliosChat from '../chat/AskHeliosChat';
import AskEnergyFluoIcon from './AskEnergyFluoIcon';
import './AskHeliosPanel.css';

/** Design-sourced "+" icon (Helios Operator Console, generic add/zoom glyph). */
const NEW_CHAT_ICON = (
  <svg className="ask-helios-svg-icon ask-helios-icon-new-chat" viewBox="0 0 16 16">
    <path d="M8 3.5v9M3.5 8h9" />
  </svg>
);

/** Design-sourced close icon (Helios Operator Console chat panel header). */
const CLOSE_ICON = (
  <svg className="ask-helios-svg-icon ask-helios-icon-close" viewBox="0 0 16 16">
    <path d="M3 3l10 10M13 3L3 13" />
  </svg>
);

/**
 * Right-side "Ask Helios" panel — the app's only chat surface, available over
 * every screen. Supersedes the old chat/dashboard toggle, the full-page
 * streaming chat view, and the context panel that went with them.
 *
 * Modelled on the Asset Details panel: a non-modal, right-docked slab with no
 * scrim, so the page behind it stays scrollable, clickable and navigable while
 * the panel is open. It persists across route changes because it is mounted
 * above the router in `AppShell`. The left edge is a drag handle for resizing
 * (see `useAskHeliosResize`).
 *
 * The conversation lives in `stores/chatStore`, not here, so closing the panel
 * (which unmounts its body) and navigating between screens both leave the thread
 * intact.
 */
export default function AskHeliosPanel() {
  const open = useShellStore(s => s.askHeliosOpen);
  const seed = useShellStore(s => s.askHeliosSeed);
  const close = useShellStore(s => s.closeAskHelios);
  const threadSeed = useChatStore(s => s.threadSeed);
  const threadOriginSeedId = useChatStore(s => s.threadOriginSeedId);
  const newChat = useChatStore(s => s.newChat);
  const resetForSeed = useChatStore(s => s.resetForSeed);
  const attachSeed = useChatStore(s => s.attachSeed);
  const { handleProps } = useAskHeliosResize();
  const [showHistory, setShowHistory] = useState(false);

  const appliedSeedId = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      appliedSeedId.current = null;
      return;
    }
    const seedId = seed?.id ?? null;
    if (seedId === appliedSeedId.current) return;
    appliedSeedId.current = seedId;
    if (!seed) return;
    if (seed.id !== threadOriginSeedId) {
      setShowHistory(false);
      resetForSeed(seed);
    } else if (seed.id !== threadSeed?.id) {
      attachSeed(seed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- threadOriginSeedId/threadSeed are read, not tracked: tracking them re-ran the reset on a chip detach
  }, [open, seed, resetForSeed, attachSeed]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  if (!open) return null;

  return (
    <aside className="ask-helios-panel" aria-label="Ask EnergyFluo" data-testid="ask-panel">
      <div className="ask-helios-resize" {...handleProps} />
      <div className="ask-helios-head">
        <span className="ask-helios-icon">
          <AskEnergyFluoIcon />
        </span>
        <div className="ask-helios-head-text">
          <div className="ask-helios-title">Ask EnergyFluo</div>
        </div>
        <button
          type="button"
          className="helios-icon-btn"
          onClick={() => {
            setShowHistory(false);
            newChat();
          }}
          aria-label="New chat"
        >
          {NEW_CHAT_ICON}
        </button>
        <div className="ask-helios-view-group">
          <button
            type="button"
            className="ask-helios-view-btn"
            onClick={() => setShowHistory(false)}
            aria-label="Chat"
            aria-pressed={!showHistory}
          >
            <Chat size={16} />
          </button>
          <button
            type="button"
            className="ask-helios-view-btn"
            onClick={() => setShowHistory(true)}
            aria-label="Conversation history"
            aria-pressed={showHistory}
          >
            <Archive size={16} />
          </button>
        </div>
        <button type="button" className="helios-icon-btn" onClick={close} aria-label="Close">
          {CLOSE_ICON}
        </button>
      </div>
      <div className="ask-helios-body">
        <AskHeliosChat showHistory={showHistory} setShowHistory={setShowHistory} />
      </div>
    </aside>
  );
}
